import type {
  CustomizationGroup,
  Product,
} from "@/lib/types";
import { restoreLocalFile, storeLocalFile } from "@/lib/local-file-storage";

export type LocalCartArtwork = {
  file: File;
  x: number;
  y: number;
  zoom: number;
};

export type LocalCartItem = {
  id: string;
  addedAt: string;
  product: Product;
  groups: CustomizationGroup[];
  selections: Record<string, string>;
  artwork: Record<string, LocalCartArtwork>;
};

export const LOCAL_CART_UPDATED_EVENT = "en-papel-cart-updated";

const databaseName = "en-papel-local-cart";
const storeName = "items";

function openCartDatabase() {
  if (typeof indexedDB === "undefined") {
    return Promise.reject(new Error("IndexedDB is unavailable."));
  }

  return new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(databaseName, 1);
    request.onupgradeneeded = () => {
      request.result.createObjectStore(storeName, { keyPath: "id" });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () =>
      reject(request.error ?? new Error("Could not open the local cart."));
  });
}

function runTransaction<T>(
  database: IDBDatabase,
  mode: IDBTransactionMode,
  action: (store: IDBObjectStore) => IDBRequest<T>,
) {
  return new Promise<T>((resolve, reject) => {
    const transaction = database.transaction(storeName, mode);
    const request = action(transaction.objectStore(storeName));
    let result: T;

    request.onsuccess = () => {
      result = request.result;
    };
    request.onerror = () =>
      reject(request.error ?? new Error("Could not access the local cart."));
    transaction.oncomplete = () => resolve(result);
    transaction.onerror = () =>
      reject(transaction.error ?? new Error("The cart transaction failed."));
    transaction.onabort = () =>
      reject(transaction.error ?? new Error("The cart transaction was aborted."));
  });
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isStringRecord(value: unknown): value is Record<string, string> {
  return (
    isRecord(value) &&
    Object.values(value).every((entry) => typeof entry === "string")
  );
}

function isProduct(value: unknown): value is Product {
  return (
    isRecord(value) &&
    typeof value.id === "string" &&
    typeof value.slug === "string" &&
    typeof value.name === "string" &&
    typeof value.basePriceCents === "number" &&
    Array.isArray(value.imageAreas) &&
    Array.isArray(value.customizationGroupIds)
  );
}

function isCustomizationGroup(value: unknown): value is CustomizationGroup {
  return (
    isRecord(value) &&
    typeof value.id === "string" &&
    typeof value.name === "string" &&
    typeof value.active === "boolean" &&
    Array.isArray(value.values) &&
    value.values.every(
      (entry) =>
        isRecord(entry) &&
        typeof entry.id === "string" &&
        typeof entry.label === "string" &&
        typeof entry.adjustmentAmount === "number",
    )
  );
}

function parseCartItem(value: unknown): LocalCartItem {
  if (
    !isRecord(value) ||
    typeof value.id !== "string" ||
    typeof value.addedAt !== "string" ||
    !isProduct(value.product) ||
    !Array.isArray(value.groups) ||
    !value.groups.every(isCustomizationGroup) ||
    !isStringRecord(value.selections) ||
    !isRecord(value.artwork)
  ) {
    throw new Error("The saved cart item is invalid.");
  }

  const artwork: Record<string, LocalCartArtwork> = {};
  for (const [areaId, entry] of Object.entries(value.artwork)) {
    if (
      !isRecord(entry) ||
      typeof entry.x !== "number" ||
      !Number.isFinite(entry.x) ||
      typeof entry.y !== "number" ||
      !Number.isFinite(entry.y) ||
      typeof entry.zoom !== "number" ||
      !Number.isFinite(entry.zoom)
    ) {
      throw new Error("The saved cart artwork is invalid.");
    }
    artwork[areaId] = {
      file: restoreLocalFile(entry.file),
      x: entry.x,
      y: entry.y,
      zoom: entry.zoom,
    };
  }

  return {
    id: value.id,
    addedAt: value.addedAt,
    product: value.product,
    groups: value.groups,
    selections: value.selections,
    artwork,
  };
}

function announceCartChange() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(LOCAL_CART_UPDATED_EVENT));
  }
}

export async function getLocalCartItems() {
  const database = await openCartDatabase();
  try {
    const stored = await runTransaction<unknown[]>(
      database,
      "readonly",
      (store) => store.getAll(),
    );
    return stored.map(parseCartItem).sort((left, right) =>
      left.addedAt.localeCompare(right.addedAt),
    );
  } finally {
    database.close();
  }
}

export async function getLocalCartItem(id: string) {
  const database = await openCartDatabase();
  try {
    const stored = await runTransaction<unknown | undefined>(
      database,
      "readonly",
      (store) => store.get(id),
    );
    return stored === undefined ? null : parseCartItem(stored);
  } finally {
    database.close();
  }
}

async function storeArtworkFiles(artwork: LocalCartItem["artwork"]) {
  const artworkEntries = await Promise.all(
    Object.entries(artwork).map(async ([areaId, entry]) => [
      areaId,
      {
        ...entry,
        file: await storeLocalFile(entry.file),
      },
    ]),
  );
  return Object.fromEntries(artworkEntries);
}

export async function addLocalCartItem(
  item: Omit<LocalCartItem, "id" | "addedAt">,
) {
  const storedItem = {
    ...item,
    artwork: await storeArtworkFiles(item.artwork),
    id: crypto.randomUUID(),
    addedAt: new Date().toISOString(),
  };
  const database = await openCartDatabase();
  try {
    await runTransaction(database, "readwrite", (store) => store.add(storedItem));
  } finally {
    database.close();
  }
  announceCartChange();
}

export async function updateLocalCartItem(
  id: string,
  item: Omit<LocalCartItem, "id" | "addedAt">,
) {
  const existing = await getLocalCartItem(id);
  if (!existing) {
    throw new Error("Este producto ya no está en el carrito.");
  }

  const storedItem = {
    ...item,
    artwork: await storeArtworkFiles(item.artwork),
    id,
    addedAt: existing.addedAt,
  };
  const database = await openCartDatabase();
  try {
    await runTransaction(database, "readwrite", (store) =>
      store.put(storedItem),
    );
  } finally {
    database.close();
  }
  announceCartChange();
}

export async function removeLocalCartItem(id: string) {
  const database = await openCartDatabase();
  try {
    await runTransaction(database, "readwrite", (store) => store.delete(id));
  } finally {
    database.close();
  }
  announceCartChange();
}
